/* ============================================================
   Aram theme — global behaviours
   Drawers, reveal-on-scroll, accordions, sticky header, cart
   AJAX (add / change / drawer refresh), variant picker, sticky
   mobile ATC. No dependencies.
   ============================================================ */
(function () {
  'use strict';

  document.documentElement.classList.remove('no-js');

  var ROUTES = (window.ARAM && window.ARAM.routes) || {
    cart_add_url: '/cart/add',
    cart_change_url: '/cart/change',
    cart_url: '/cart',
  };
  var MONEY_FORMAT = (window.ARAM && window.ARAM.moneyFormat) || '£{{amount}}';
  var FREE_SHIPPING_THRESHOLD = (window.ARAM && window.ARAM.freeShippingThreshold) || 0; // pence; 0 = off

  /* ---------- Utilities ---------- */
  function formatMoney(cents) {
    // Keep two decimals so JS output matches Liquid's `money` filter ("£40.00").
    var value = (cents / 100).toFixed(2);
    return MONEY_FORMAT.replace(/\{\{\s*amount[^}]*\}\}/, value);
  }

  function dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
  }

  /* ---------- Reveal on scroll ---------- */
  var revealEls = document.querySelectorAll('.reveal');
  if ('IntersectionObserver' in window && revealEls.length) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    revealEls.forEach(function (el) { io.observe(el); });
  } else {
    revealEls.forEach(function (el) { el.classList.add('is-visible'); });
  }

  /* ---------- Sticky header state ---------- */
  var header = document.querySelector('.site-header');
  if (header) {
    var onScroll = function () {
      header.classList.toggle('is-stuck', window.scrollY > 24);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Drawers ---------- */
  var openDrawer = null;
  var lastTrigger = null;

  function setDrawer(id, open, trigger) {
    var drawer = document.getElementById(id);
    if (!drawer) return;
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    document.body.style.overflow = open ? 'hidden' : '';
    if (open) {
      openDrawer = drawer;
      lastTrigger = trigger || null;
      var focusable = drawer.querySelector('button, a, input, [tabindex]');
      if (focusable) setTimeout(function () { focusable.focus(); }, 120);
    } else {
      if (openDrawer === drawer) openDrawer = null;
      if (lastTrigger) { lastTrigger.focus(); lastTrigger = null; }
    }
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      setDrawer(opener.getAttribute('data-drawer-open'), true, opener);
      return;
    }
    var closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      e.preventDefault();
      var d = closer.closest('.drawer');
      if (d) setDrawer(d.id, false);
      return;
    }
    if (e.target.classList && e.target.classList.contains('drawer-backdrop')) {
      var dd = e.target.closest('.drawer');
      if (dd) setDrawer(dd.id, false);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openDrawer) setDrawer(openDrawer.id, false);
  });

  /* ---------- Accordions ---------- */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('.accordion-trigger');
    if (!trigger) return;
    var expanded = trigger.getAttribute('aria-expanded') === 'true';
    var panel = document.getElementById(trigger.getAttribute('aria-controls'));
    trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    if (panel) {
      panel.style.maxHeight = expanded ? '0px' : panel.scrollHeight + 'px';
    }
  });

  /* ---------- Cart ---------- */
  function refreshCartUI() {
    return fetch(ROUTES.cart_url + '.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.classList.toggle('hidden', cart.item_count === 0);
        });
        document.querySelectorAll('[data-cart-subtotal]').forEach(function (el) {
          el.textContent = formatMoney(cart.items_subtotal_price);
        });
        updateFreeShipping(cart.items_subtotal_price);
        return refreshDrawerContents().then(function () { return cart; });
      });
  }

  function refreshDrawerContents() {
    var container = document.querySelector('[data-cart-drawer-items]');
    if (!container) return Promise.resolve();
    return fetch(ROUTES.cart_url + '?section_id=cart-drawer')
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-cart-drawer-items]');
        if (fresh) container.innerHTML = fresh.innerHTML;
      })
      .catch(function () { /* section render unavailable; count still updates */ });
  }

  function updateFreeShipping(subtotal) {
    if (!FREE_SHIPPING_THRESHOLD) return;
    document.querySelectorAll('[data-free-shipping]').forEach(function (bar) {
      var msg = bar.querySelector('[data-free-shipping-msg]');
      var fill = bar.querySelector('.progress-fill');
      var remaining = FREE_SHIPPING_THRESHOLD - subtotal;
      var pct = Math.min(100, Math.round((subtotal / FREE_SHIPPING_THRESHOLD) * 100));
      if (fill) fill.style.width = pct + '%';
      if (msg) {
        msg.textContent = remaining > 0
          ? (bar.getAttribute('data-msg-remaining') || 'Spend {amount} more for free UK delivery').replace('{amount}', formatMoney(remaining))
          : (bar.getAttribute('data-msg-unlocked') || 'Free UK delivery unlocked');
      }
    });
  }

  function addToCart(formOrData, button) {
    if (button) button.classList.add('is-loading');
    // Reset any stale error messages from a previous attempt.
    document.querySelectorAll('[data-cart-error]').forEach(function (el) {
      el.textContent = '';
      el.classList.add('hidden');
    });
    var body = formOrData instanceof HTMLFormElement ? new FormData(formOrData) : (function () {
      var fd = new FormData();
      fd.append('id', formOrData.id);
      fd.append('quantity', formOrData.quantity || 1);
      return fd;
    })();
    return fetch(ROUTES.cart_add_url + '.js', { method: 'POST', body: body })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (err) { throw err; });
        return r.json();
      })
      .then(function () { return refreshCartUI(); })
      .then(function (cart) {
        setDrawer('CartDrawer', true, button || null);
        dispatch('aram:cart:added', { cart: cart });
      })
      .catch(function (err) {
        var msg = (err && (err.description || err.message)) || 'Could not add to cart';
        // Prefer an error element near the trigger (its drawer or form); fall back to any.
        var scope = (button && button.closest('.drawer, form')) || null;
        var status = (scope && scope.querySelector('[data-cart-error]')) || document.querySelector('[data-cart-error]');
        if (status) {
          status.textContent = msg;
          status.classList.remove('hidden');
          // If the message lives inside a closed drawer, open it so the error is visible.
          var drawer = status.closest('.drawer');
          if (drawer && !drawer.classList.contains('is-open')) setDrawer(drawer.id, true, button || null);
        }
      })
      .finally(function () { if (button) button.classList.remove('is-loading'); });
  }

  // Product form submit (PDP + quick add)
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    addToCart(form, form.querySelector('[type="submit"]'));
  });

  // Quick add buttons with a fixed variant id
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    addToCart({ id: btn.getAttribute('data-quick-add'), quantity: 1 }, btn);
  });

  // Cart line quantity change / remove (event delegation; lines re-render)
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-line-change]');
    if (!btn) return;
    e.preventDefault();
    var line = parseInt(btn.getAttribute('data-line'), 10);
    var qty = parseInt(btn.getAttribute('data-line-change'), 10);
    fetch(ROUTES.cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: qty }),
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        // On the cart PAGE, reload so totals/lines re-render server-side.
        if (document.body.getAttribute('data-template') === 'cart') { window.location.reload(); return; }
        refreshCartUI();
      });
  });

  // Cart-page quantity input typed directly (data-line-input carries data-line)
  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-line-input]');
    if (!input) return;
    var line = parseInt(input.getAttribute('data-line'), 10);
    var qty = Math.max(0, parseInt(input.value, 10) || 0);
    fetch(ROUTES.cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: qty }),
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        if (document.body.getAttribute('data-template') === 'cart') { window.location.reload(); return; }
        refreshCartUI();
      });
  });

  /* ---------- Quantity steppers ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-qty-step]');
    if (!btn) return;
    var input = btn.closest('.qty-stepper').querySelector('input');
    var step = parseInt(btn.getAttribute('data-qty-step'), 10);
    var next = Math.max(1, (parseInt(input.value, 10) || 1) + step);
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Variant picker (radio pills) ---------- */
  document.querySelectorAll('[data-variant-json]').forEach(function (script) {
    var wrapper = script.closest('[data-product-wrapper]') || document;
    var variants;
    try { variants = JSON.parse(script.textContent); } catch (err) { return; }
    var form = wrapper.querySelector('form[data-product-form]');
    if (!form) return;
    var idInput = form.querySelector('input[name="id"]');

    function currentOptions() {
      var opts = [];
      wrapper.querySelectorAll('.variant-pills').forEach(function (group) {
        var checked = group.querySelector('input:checked');
        if (checked) opts.push(checked.value);
      });
      return opts;
    }

    function onChange() {
      var opts = currentOptions();
      var match = variants.find(function (v) {
        return opts.every(function (val, i) { return v.options[i] === val; });
      });
      if (!match) return;
      idInput.value = match.id;
      var priceEl = wrapper.querySelector('[data-price]');
      if (priceEl) priceEl.textContent = formatMoney(match.price);
      var stickyPrice = document.querySelector('.sticky-atc-price');
      if (stickyPrice) stickyPrice.textContent = formatMoney(match.price);
      // In-form submit button plus any buttons linked via the form attribute
      // (e.g. the sticky mobile ATC bar).
      var buttons = [];
      var submit = form.querySelector('[type="submit"]');
      if (submit) buttons.push(submit);
      if (form.id) {
        document.querySelectorAll('button[form="' + form.id + '"]').forEach(function (b) {
          if (buttons.indexOf(b) === -1) buttons.push(b);
        });
      }
      buttons.forEach(function (btn) {
        btn.disabled = !match.available;
        var label = btn.querySelector('[data-atc-label]') || btn;
        label.textContent = match.available
          ? (btn.getAttribute('data-label-add') || (submit && submit.getAttribute('data-label-add')) || 'Add to cart')
          : (btn.getAttribute('data-label-soldout') || (submit && submit.getAttribute('data-label-soldout')) || 'Sold out');
      });
      var url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url.toString());
      dispatch('aram:variant:change', { variant: match });
    }

    wrapper.querySelectorAll('.variant-pills input').forEach(function (input) {
      input.addEventListener('change', onChange);
    });
  });

  /* ---------- PDP gallery thumbs ---------- */
  document.addEventListener('click', function (e) {
    var thumb = e.target.closest('.pdp-thumb');
    if (!thumb) return;
    var gallery = thumb.closest('.pdp-gallery');
    var main = gallery && gallery.querySelector('.media-main img');
    if (!main) return;
    var src = thumb.getAttribute('data-full-src');
    var srcset = thumb.getAttribute('data-full-srcset');
    if (src) main.src = src;
    if (srcset) main.srcset = srcset;
    var alt = thumb.querySelector('img');
    if (alt) main.alt = alt.alt;
    gallery.querySelectorAll('.pdp-thumb').forEach(function (t) { t.classList.remove('is-active'); });
    thumb.classList.add('is-active');
  });

  /* ---------- Sticky mobile ATC ---------- */
  var stickyAtc = document.querySelector('.sticky-atc');
  var buybox = document.querySelector('[data-buybox-sentinel]');
  if (stickyAtc && buybox && 'IntersectionObserver' in window) {
    var atcIo = new IntersectionObserver(function (entries) {
      stickyAtc.classList.toggle('is-visible', !entries[0].isIntersecting);
    }, { threshold: 0 });
    atcIo.observe(buybox);
  }

  /* ---------- Marquee duplication (seamless loop) ---------- */
  document.querySelectorAll('.marquee-track[data-duplicate]').forEach(function (track) {
    track.innerHTML += track.innerHTML;
  });

  /* ---------- Newsletter (Shopify customer form posts natively) ---------- */
  // Status messages are server-rendered by Liquid on redirect; nothing needed here.

  /* Expose minimal API */
  window.ARAM = window.ARAM || {};
  window.ARAM.addToCart = addToCart;
  window.ARAM.refreshCartUI = refreshCartUI;
  window.ARAM.openDrawer = function (id) { setDrawer(id, true); };
  window.ARAM.formatMoney = formatMoney;

  // Initial free-shipping state
  refreshCartUI();
})();
